/**
 * Assessment.jsx — Individual Workspace assessment page (P4).
 *
 * Three widgets stacked on one page:
 *   1. MySkillProfile  — one row per capability with level bar +
 *                        evidence count. Domain is chosen at top;
 *                        server returns all 17 (or however many)
 *                        active capabilities LEFT-JOINed against
 *                        skill_profile so uncovered capabilities
 *                        show current_level = 0.
 *   2. TakeSkillCheck  — one task at a time. Learner types their
 *                        answer, submit routes to platform/grader,
 *                        result appears inline (confirmed / awaiting
 *                        review / rejected with reason).
 *   3. MyRoadmap       — latest roadmap for chosen role. Ordered
 *                        steps with from→target level, resource
 *                        chip, status. "Open Tutor" button placeholder
 *                        per PHASES §P4.4; wiring to platform/tutor
 *                        happens in P4b (Tutor chat surface).
 *
 * Styling: matches Dashboard.js — Tailwind + lucide-react. CTAs use
 * the brand cyan #00BCD4 (memory: project_brand_tokens.md).
 *
 * ⚠️  UI NOT VERIFIED IN A BROWSER. The dev server has not been
 * started in this environment. Layout compiles against the same
 * lucide-react + Tailwind used by Dashboard.js:29-34 but the
 * clickthrough was not exercised. P4 report calls this out; a
 * manual smoke is required before promoting to production.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowRight, CheckCircle, Clock, XCircle, RefreshCw,
  Target, Book, MessageSquare, Award,
} from 'lucide-react';
import assessmentApi from '../services/assessmentApi';
import LoadingSpinner from '../components/common/LoadingSpinner';

const CYAN = '#00BCD4';

// ─── level-bar helper ────────────────────────────────────────────────
function LevelBar({ level }) {
  // 5 segments (0..4). Filled up to `level`. Uses brand cyan.
  return (
    <div className="flex gap-1">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="h-2 w-6 rounded"
          style={{ backgroundColor: i <= level ? CYAN : '#E5E7EB' }}
          aria-hidden="true"
        />
      ))}
      <span className="ml-2 text-xs text-gray-500">{level}/4</span>
    </div>
  );
}

// ─── widget 1: MySkillProfile ───────────────────────────────────────
function MySkillProfile({ domainId }) {
  const [profile, setProfile] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!domainId) return;
    setLoading(true); setError(null);
    try {
      const rows = await assessmentApi.getProfile({ domainId });
      setProfile(rows);
    } catch (err) {
      setError(err.message || 'Failed to load profile');
    } finally { setLoading(false); }
  }, [domainId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingSpinner message="Loading skill profile…" centered />;
  if (error) return <div className="p-4 text-sm text-red-600">Error: {error}</div>;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <Award size={20} style={{ color: CYAN }} />
        <h2 className="text-lg font-semibold text-gray-900">My skill profile</h2>
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-gray-500">
          <tr>
            <th className="py-2">Capability</th>
            <th className="py-2">Level</th>
            <th className="py-2">Evidence</th>
          </tr>
        </thead>
        <tbody>
          {profile.map((row) => (
            <tr key={row.capability_id} className="border-t border-gray-100">
              <td className="py-2">
                <div className="font-mono text-xs text-gray-500">{row.capability_code}</div>
                <div className="text-gray-800">{row.capability_statement}</div>
              </td>
              <td className="py-2"><LevelBar level={Number(row.current_level) || 0} /></td>
              <td className="py-2 text-gray-600">{row.evidence_count || 0}</td>
            </tr>
          ))}
          {profile.length === 0 && (
            <tr><td className="py-4 text-gray-500" colSpan={3}>No capabilities loaded yet for this domain.</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

// ─── widget 2: TakeSkillCheck ───────────────────────────────────────
function TakeSkillCheck({ domainId, roleSpecId, onEvidenceChange }) {
  const [task, setTask] = useState(null);
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const loadNext = useCallback(async () => {
    if (!domainId) return;
    setLoading(true); setError(null); setResult(null);
    try {
      const next = await assessmentApi.getNextTask({ domainId, roleSpecId });
      setTask(next);
      setAnswer('');
    } catch (err) {
      setError(err.message || 'Failed to load next task');
    } finally { setLoading(false); }
  }, [domainId, roleSpecId]);

  useEffect(() => { loadNext(); }, [loadNext]);

  const submit = async () => {
    if (!task || !answer.trim()) return;
    setSubmitting(true); setError(null);
    try {
      const res = await assessmentApi.submitAnswer({ taskId: task.task_id, answer: answer.trim() });
      setResult(res);
      if (onEvidenceChange) onEvidenceChange();   // let the profile widget refresh
    } catch (err) {
      setError(err.message || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <Target size={20} style={{ color: CYAN }} />
        <h2 className="text-lg font-semibold text-gray-900">Take a skill check</h2>
      </div>
      {loading && <LoadingSpinner message="Picking your next task…" centered />}
      {!loading && !task && (
        <div className="p-4 text-sm text-gray-600">
          You've confirmed evidence for every capability in this domain. Take a break, or
          <button onClick={loadNext} className="ml-1 underline" style={{ color: CYAN }}>refresh</button>.
        </div>
      )}
      {!loading && task && (
        <>
          <div className="mb-4">
            <div className="mb-2 flex items-center gap-2 text-xs text-gray-500">
              <span className="font-mono">{task.capability_code}</span>
              <span>·</span>
              <span>{task.task_type}</span>
              <span>·</span>
              <span>max level {task.max_level_provable}</span>
            </div>
            <div className="text-sm text-gray-800">{task.capability_statement}</div>
            <pre className="mt-3 whitespace-pre-wrap rounded bg-gray-50 p-3 text-sm text-gray-800">
              {task.task_prompt}
            </pre>
          </div>
          <textarea
            className="w-full rounded border border-gray-300 p-3 text-sm focus:border-cyan-500 focus:outline-none"
            rows={8}
            placeholder="Your answer…"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            disabled={submitting}
          />
          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={submit}
              disabled={submitting || !answer.trim()}
              className="rounded px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              style={{ backgroundColor: CYAN }}
            >
              {submitting ? 'Submitting…' : 'Submit answer'}
              <ArrowRight size={14} className="ml-2 inline-block" />
            </button>
            <button onClick={loadNext} className="text-sm text-gray-600 underline" disabled={submitting}>
              Skip and get another
            </button>
          </div>
          {error && <div className="mt-3 text-sm text-red-600">Error: {error}</div>}
          {result && (
            <ResultBanner result={result} />
          )}
        </>
      )}
    </section>
  );
}

function ResultBanner({ result }) {
  if (result.result === 'confirmed') {
    return (
      <div className="mt-3 flex items-center gap-2 rounded bg-green-50 p-3 text-sm text-green-800">
        <CheckCircle size={16} />
        Confirmed at level {result.confirmed_level}.
        {result.capped && ' (Grader proposed higher, capped to task max.)'}
      </div>
    );
  }
  if (result.result === 'proposed') {
    return (
      <div className="mt-3 flex items-center gap-2 rounded bg-yellow-50 p-3 text-sm text-yellow-800">
        <Clock size={16} />
        Proposed at level {result.effective_level}. Awaiting human review before it counts.
      </div>
    );
  }
  if (result.result === 'rejected') {
    return (
      <div className="mt-3 flex items-center gap-2 rounded bg-red-50 p-3 text-sm text-red-800">
        <XCircle size={16} />
        Rejected — {result.reason}. Try again with different phrasing or a fuller answer.
      </div>
    );
  }
  return null;
}

// ─── widget 3: MyRoadmap ────────────────────────────────────────────
function MyRoadmap({ domainId, roleSpecId, refreshToken }) {
  const [roadmap, setRoadmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!roleSpecId) return;
    setLoading(true); setError(null);
    try {
      const rm = await assessmentApi.getRoadmap({ roleSpecId });
      setRoadmap(rm);
    } catch (err) {
      setError(err.message || 'Failed to load roadmap');
    } finally { setLoading(false); }
  }, [roleSpecId]);

  useEffect(() => { load(); }, [load, refreshToken]);

  const regen = async () => {
    setRegenerating(true); setError(null);
    try {
      await assessmentApi.regenerateRoadmap({ domainId, roleSpecId });
      await load();
    } catch (err) {
      setError(err.message || 'Failed to regenerate');
    } finally { setRegenerating(false); }
  };

  if (loading) return <LoadingSpinner message="Loading roadmap…" centered />;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Book size={20} style={{ color: CYAN }} />
          <h2 className="text-lg font-semibold text-gray-900">My roadmap</h2>
          {roadmap && (
            <span className="ml-2 text-xs text-gray-500">v{roadmap.version} · {roadmap.items.length} steps</span>
          )}
        </div>
        <button
          onClick={regen}
          disabled={regenerating || !roleSpecId}
          className="flex items-center gap-1 rounded border border-gray-300 px-3 py-1 text-sm text-gray-700 disabled:opacity-50"
        >
          <RefreshCw size={14} />
          {regenerating ? 'Regenerating…' : 'Regenerate'}
        </button>
      </div>
      {error && <div className="mb-3 text-sm text-red-600">Error: {error}</div>}
      {!roadmap && (
        <div className="p-4 text-sm text-gray-600">
          No roadmap yet — click <span className="font-medium">Regenerate</span> to build one from your current evidence.
        </div>
      )}
      {roadmap && (
        <ol className="space-y-2">
          {roadmap.items.map((step) => (
            <li key={step.id} className="flex items-center gap-3 rounded border border-gray-100 p-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-700">
                {step.step_order}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-gray-500">{step.capability_code}</span>
                  <span className="text-sm text-gray-800">level {step.from_level} → {step.target_level}</span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                  <span className="rounded bg-gray-100 px-2 py-0.5">{step.resource_type}</span>
                  {step.resource_ref
                    ? <span className="font-mono">{step.resource_ref}</span>
                    : <span className="italic text-gray-400">no specific resource yet</span>}
                  <span>·</span>
                  <span className="uppercase">{step.status}</span>
                </div>
              </div>
              {/* Placeholder Tutor CTA — Tutor chat wire lands in P4b. */}
              <button
                className="flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 disabled:opacity-50"
                disabled
                title="Tutor chat wiring lands in P4b"
              >
                <MessageSquare size={12} />
                Ask Tutor
              </button>
            </li>
          ))}
          {roadmap.items.length === 0 && (
            <li className="text-sm text-gray-600">No gaps — you're at target on every capability for this role.</li>
          )}
        </ol>
      )}
    </section>
  );
}

// ─── page shell ─────────────────────────────────────────────────────
export default function Assessment() {
  // Domain and role picker is out of scope for P4a — hard-code the
  // iseries domain and let the caller supply a role_spec_id via query
  // string until the picker lands. This keeps the widgets exercisable
  // for demo/QA without waiting on the picker UI.
  const params = new URLSearchParams(window.location.search);
  const domainId    = params.get('domainId')    || '';
  const roleSpecId  = params.get('roleSpecId')  || '';
  const [refreshToken, setRefreshToken] = useState(0);
  const bumpRefresh = () => setRefreshToken((n) => n + 1);

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold text-gray-900">Skill assessment</h1>
        <p className="mt-1 text-sm text-gray-600">
          Take a skill check to build evidence, track your capability profile, and view
          the deterministic roadmap for your chosen role.
        </p>
        {(!domainId || !roleSpecId) && (
          <div className="mt-3 rounded bg-yellow-50 p-3 text-sm text-yellow-800">
            Pass <code>?domainId=…&amp;roleSpecId=…</code> in the URL to load a real domain and role.
            (Picker UI is out of scope for P4a — lands in P4b.)
          </div>
        )}
      </header>
      {domainId && <MySkillProfile domainId={domainId} key={`profile-${refreshToken}`} />}
      {domainId && <TakeSkillCheck domainId={domainId} roleSpecId={roleSpecId} onEvidenceChange={bumpRefresh} />}
      {domainId && roleSpecId && <MyRoadmap domainId={domainId} roleSpecId={roleSpecId} refreshToken={refreshToken} />}
    </div>
  );
}
